import Inflector from './Inflector.js';
import { shouldShowBye } from './../helpers/DsHelper.js';

/**
 * @param {object} player
 * @param {object} options
 * @param {boolean} options.forExport Whether to include the player's nickname in the export format
 * @param {boolean} options.includeNickName Whether to include the player's nickname in the name
 * @param {boolean} options.useFirstInitial Whether to use the first initial of the player's first name
 * @return {string} The first and last name of the πlayer
 */
export const getFullName = function (player, options = {}) {
    if (!player) {
        return '';
    }

    const { forExport = false, includeNickName = true, useFirstInitial = false } = options;

    let fullName = [player.first_name, player.last_name];
    if (useFirstInitial) {
        fullName[0] = fullName[0].slice(0, 1) + '.';
    }
    if (player.nick_name && includeNickName) {
        if (forExport) {
            fullName.splice(1, 0, "'" + player.nick_name + "'");
        } else {
            fullName.splice(1, 0, '“' + player.nick_name + '”');
        }
    }
    return fullName.join(' ');
};

export const getAbbrName = function (player) {
    return player.first_name.charAt(0) + '. ' + player.last_name;
};

/**
 * @param {object} Player
 * @param {object} options
 * @param {string} options.teamAbbr A custom team abbreviation
 * @param {boolean} options.raw Whether to return the raw text or HTML markup
 * @param {boolean} options.abbreviate Whether to abbreviate the player name
 * @param {object} options.getFullNameOptions Options for the getFullName function
 * which is used if `abbreviate` is false.
 * @param {boolean} options.getFullNameOptions.forExport Whether to include the
 * player's nickname in the export format
 * @param {boolean} options.getFullNameOptions.includeNickName Whether to include
 * the player's nickname in the name
 * @param {boolean} options.getFullNameOptions.useFirstInitial Whether to use the
 * first initial of the player's first name
 * @return {string} The first and last name, position and team of the player
 */
export const getNamePositionTeam = function (player, options = {}) {
    let { teamAbbr = null, raw = false, abbreviate = false, getFullNameOptions = {} } = options;
    if (!teamAbbr) {
        teamAbbr = player.team ? player.team.abbr : player.team_abbr;
    }
    let playerName = abbreviate ? getAbbrName(player) : getFullName(player, getFullNameOptions);
    return !raw
        ? '<span class="name">' +
              playerName +
              '</span> <span class="position">' +
              player.position +
              '</span> <span class="team">' +
              teamAbbr +
              '</span>'
        : playerName + ', ' + player.position + ' ' + teamAbbr;
};

/**
 * This could probably be moved to a team helper file
 * @param {object} player
 * @returns {string} The bye week for the player's team, or N/A if the bye week is null or if the current month is before May
 * @see league/helpers/DsValue.js for getPlayerBye() also.
 */
export const getByeWeek = function (player) {
    if (player.team.bye === null || !shouldShowBye()) {
        return 'N/A';
    }
    return player.team.bye;
};

/**
 * Player has no match in our database.
 *
 * @param {object} player
 * @return {boolean} True if player only exists in the user’s league.
 */
export const isUnknownPlayer = function (player) {
    if (!player) {
        return false;
    }

    if (typeof player.is_unknown_player !== 'undefined' && player.is_unknown_player !== null) {
        return Boolean(player.is_unknown_player);
    }

    // --- Old way to flag this
    const playerId = Number(player.player_id || player.id || 0);

    return playerId > 999999;
};

/**
 * @param {object} Player
 * @return {string} The URL to the player page for the player
 */
export const getUrl = function (player) {
    return '/fantasy/points-outlook/' + Inflector.slug(getFullName(player)) + '/' + (player.player_id ? player.player_id : player.id);
};

export const getSharkBiteUrl = function (player) {
    return '/fantasy-football-news/' + Inflector.slug(getFullName(player)) + '/' + (player.player_id ? player.player_id : player.id);
};

export const getPlayerLink = player => `<a href="${getUrl(player)}">${getNamePositionTeam(player)}</a>`;

/**
 * Returns the age of a player based on their birthday.
 * @param {string} birthday
 * @returns {string}
 */
export const getAge = function (birthday) {
    const today = new Date();
    const date = new Date(birthday);
    const diffTime = Math.abs(today - date);
    return diffTime / (1000 * 60 * 60 * 24 * 365);
};

/**
 * Returns an array of players from a provided array of players whose names
 * the given search phrase
 * @param {array} players
 * @param {string} searchPhrase
 * @returns {array}
 */
export const getPlayersMatchingSearchPhrase = (players, searchPhrase) => {
    const matchingPlayers = [];
    for (var i = 0; i < players.length; i++) {
        if (playerIsMatchForSearchPhrase(players[i], searchPhrase)) {
            matchingPlayers.push(players[i]);
        }
    }
    return matchingPlayers;
};

/**
 * Checks whether a player's name would be a match for a search phrase
 * @param {object} player Represents a PlayerData type object
 * @param {string} searchPhrase A search phrase to match against player name
 * @returns {boolean}
 */
export const playerIsMatchForSearchPhrase = (player, searchPhrase) => {
    const nameParts = [player.first_name, player.last_name];

    if (player.nick_name) {
        nameParts.splice(1, 0, '“' + player.nick_name + '”');
    }

    const fullName = nameParts.join(' ').toLowerCase();

    return fullName.includes(searchPhrase.toLowerCase());
};

/**
 * Gets the URL for a player's headshot
 * @param {object} player Either a `player` record or data in the PlayerData
 * format for tools
 * @param {string} variation The variation to load (image size)
 * @returns {string}
 */
export const getHeadshotUrl = (player, variation) => {
    if (variation != '1200x1200' && variation != '1200x675' && variation != '675x675') {
        variation = '1200x1200';
    }
    return '/player-images/' + player.headshot_id + '/' + variation + '.avif';
};

export default {
    getAge,
    getFullName,
    getNamePositionTeam,
    getUrl,
    getPlayerLink,
    getByeWeek,
    getSharkBiteUrl,
};
